<template>
  <el-dialog ref="dialog" :visible="visible" title="恢复图片上一版" width="min(600px, calc(100vw - 24px))" append-to-body custom-class="image-resource-restore-dialog" :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" :before-close="close" @opened="focusCancel" @closed="$emit('closed')" @keydown.tab.native="trapFocus">
    <div class="image-restore-body" :aria-busy="String(loading || saving)">
      <p v-if="loading" role="status">正在读取上一版…</p>
      <template v-if="preview">
        <p>当前“{{ preview.current.name }}”为 v{{ preview.current.version }}，将恢复上一版“{{ preview.previous.name }}”（v{{ preview.previous.version }}），保存为 v{{ preview.current.version + 1 }}。</p>
        <div class="image-restore-preview"><img v-if="!previewFailed" :src="preview.previous.url" :alt="'待恢复的上一版：' + preview.previous.name" @error="previewFailed = true"><p v-else role="alert">上一版图片加载失败，请检查后端文件；重新读取后再确认。</p></div>
        <p>恢复该版图片文件、名称、说明和分类；可见范围保持不变。历史版本和文件保留，已保存大屏、模板中使用的固定图片版本不会改变。</p>
        <p v-if="!canPublishCurrent" role="status">当前图片为公开图片，恢复还需要图片资源的“公开”权限。</p>
        <p v-if="preview.previous.description">{{ preview.previous.description }}</p>
      </template>
      <p v-if="error" class="image-restore-error" role="alert">{{ error }}</p>
      <el-button v-if="(!preview && error || previewFailed) && !loading && !saving && !blocked" size="small" @click="load">重新读取上一版</el-button>
      <p v-if="saving" role="status">正在恢复，请勿重复提交…</p>
    </div>
    <span slot="footer" class="image-restore-actions"><el-button ref="cancel" :disabled="saving" @click="close">{{ blocked ? '关闭并刷新列表' : '取消' }}</el-button><el-button type="primary" :loading="saving" :disabled="!preview || previewFailed || loading || saving || blocked || !canSave || !canPublishCurrent" @click="restore">确认恢复上一版</el-button></span>
  </el-dialog>
</template>
<script>
import store from '@/store'
import { getPreviousImage, restoreImage } from '@/api/image-resources'
import { imageRestorePreview, restoredImage, imageError } from '@/utils/image-resources'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'

export default {
  props: { visible: Boolean, imageId: { type: String, required: true } },
  data: () => ({ preview: null, previewFailed: false, loading: false, saving: false, blocked: false, error: '' }),
  computed: {
    canSave () { return store.getters['auth/hasResourcePermission']('visualizationImage:query') && store.getters['auth/hasResourcePermission']('visualizationImage:save') },
    canPublishCurrent () { return !this.preview || this.preview.current.visibility !== 'public' || store.getters['auth/hasResourcePermission']('visualizationImage:publish') }
  },
  watch: {
    visible (value) { if (value) this.load(); else this.cancelRead() },
    imageId () { if (this.visible) this.load() },
    canSave (value) { if (!value) { this.cancelRead(); this.$emit('update:visible', false) } }
  },
  created () { this._read = 0; if (this.visible) this.load() },
  mounted () { if (this.visible) this.$nextTick(this.focusCancel) },
  beforeDestroy () { this.cancelRead() },
  methods: {
    cancelRead () { this._read++; if (this._cancel) this._cancel.cancel() },
    focusCancel () { if (!this._isDestroyed && this.$refs.cancel) this.$refs.cancel.$el.focus() },
    trapFocus (event) {
      if (!this.$refs.dialog) return
      const controls = Array.from(this.$refs.dialog.$el.querySelectorAll('button:not([disabled]), [tabindex="0"]')).filter(el => el.getClientRects().length)
      if (!controls.length) { event.preventDefault(); return }
      const first = controls[0], last = controls[controls.length - 1]
      if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus() }
    },
    close () {
      if (this.saving) return
      this.cancelRead(); this.$emit('update:visible', false)
      if (this.blocked) this.$emit('check')
    },
    async load () {
      if (this.saving || this.blocked) return
      this.cancelRead(); const request = this._read, id = this.imageId
      this.preview = null; this.previewFailed = false; this.error = ''
      if (!this.canSave || !this.visible) return
      this._cancel = createRequestCancellation(); this.loading = true
      try {
        const preview = imageRestorePreview(await getPreviousImage(id, { cancelToken: this._cancel.token }), id)
        if (request !== this._read || this._isDestroyed || !this.visible || !this.canSave || id !== this.imageId) return
        this.preview = preview
      } catch (error) {
        if (request === this._read && !this._isDestroyed && !isRequestCanceled(error)) this.error = error.status === 404 ? '图片不可访问或恢复接口尚未部署，请刷新列表并确认已更新后端。' : imageError(error)
      } finally { if (request === this._read && !this._isDestroyed) this.loading = false }
    },
    async restore () {
      if (!this.preview || this.previewFailed || this.loading || this.saving || this.blocked || !this.visible || !this.canSave || !this.canPublishCurrent) return
      const preview = this.preview, id = this.imageId, request = this._read
      this.saving = true; this.error = ''
      try {
        const response = await restoreImage(id, { revision: preview.current.revision, version: preview.previous.version })
        this.blocked = true
        const item = restoredImage(response, preview)
        if (request !== this._read || this._isDestroyed || id !== this.imageId || !this.visible || !this.canSave) return
        this.blocked = false; this.$emit('restored', item); this.$emit('update:visible', false)
      } catch (error) {
        if (request !== this._read || this._isDestroyed || id !== this.imageId || !this.visible || !this.canSave) return
        // 不确定的写入不能自动重发；关闭刷新后重新核对当前版本。
        this.blocked = this.blocked || !error.status || error.status >= 500 || [401, 403, 404, 409].includes(error.status)
        this.error = this.blocked ? (error.status === 409 ? '图片已变化或历史不可用，本次未确认恢复；请关闭并刷新列表重新核对。' : '恢复结果或权限未确认，请关闭并刷新列表检查，勿重复提交。') : imageError(error)
        if (this.blocked) this.$nextTick(this.focusCancel)
      } finally { if (!this._isDestroyed) this.saving = false }
    }
  }
}
</script>
<style>
.image-resource-restore-dialog { background: #fff !important; color: #1f2937; border-radius: 6px; }
.image-resource-restore-dialog .el-dialog__title, .image-resource-restore-dialog .el-dialog__body { color: #1f2937; }
.image-resource-restore-dialog .image-restore-body { max-height: 50vh; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: none; -ms-overflow-style: none; }
.image-resource-restore-dialog .image-restore-body::-webkit-scrollbar { display: none; }
.image-resource-restore-dialog p { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.7; }
.image-resource-restore-dialog .image-restore-preview { display: flex; align-items: center; justify-content: center; height: 180px; background: #f4f5f7; border: 1px solid #e4e7ed; border-radius: 4px; }
.image-resource-restore-dialog .image-restore-preview img { max-width: 100%; max-height: 100%; object-fit: contain; }
.image-resource-restore-dialog .image-restore-error { color: #b42318; }
.image-resource-restore-dialog .image-restore-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; padding-bottom: env(safe-area-inset-bottom); }
.image-resource-restore-dialog .el-button { min-height: 44px; margin-left: 0; border-radius: 4px; }
.image-resource-restore-dialog .el-button--primary { color: #fff; background: #1677ff; border-color: #1677ff; }
.image-resource-restore-dialog .el-button--default { color: #1f2937; background: #fff; border-color: #dcdfe6; }
</style>
